import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LanguageSelect } from '@/components/LanguageSelect';
import { useT } from '@/i18n';
import { formatNYClock } from '@/market/marketHours';
import { settingsStore, useSettings } from '@/settings/settingsStore';
import { useAppState } from '@/state/appStore';
import { DISPLAY_CURRENCIES, type Currency } from '@/types';
import { IconBack, IconDownload, IconGear } from './Icons';
import s from './Header.module.css';

interface HeaderProps {
  crumbs?: { label: string; to?: string }[];
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  showCurrency?: boolean;
  onExport?: () => void;
}

export function Header({ crumbs, title, subtitle, actions, showCurrency = true, onExport }: HeaderProps) {
  const { t } = useT();
  const settings = useSettings();
  const app = useAppState();
  const navigate = useNavigate();

  return (
    <header className={s.header}>
      <div className={s.left}>
        {crumbs ? (
          <>
            <Link to={crumbs[0].to ?? '/'} className={s.back}>
              <IconBack />
              {crumbs[0].label}
            </Link>
            {crumbs.slice(1).map((c, i) => (
              <span key={i} className={s.crumb}>
                <span className="muted">/</span>
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span className="muted">{c.label}</span>}
              </span>
            ))}
            {title && (
              <>
                <span className="muted">/</span>
                <span className={`serif ${s.title}`}>{title}</span>
              </>
            )}
          </>
        ) : (
          <>
            <Link to="/" className={`serif ${s.brand}`}>{t('app.name')}</Link>
            <span className={`muted ${s.subtitle}`}>{subtitle ?? t('header.subtitle')}</span>
          </>
        )}
      </div>
      <div className={s.right}>
        {actions}
        {app.lastRefreshAt && (
          <span className={`muted num ${s.clock}`}>
            {t('header.updated', { time: formatNYClock(app.lastRefreshAt) })}
            {app.refreshing ? t('header.refreshing') : ''}
          </span>
        )}
        {showCurrency && (
          <label className={`btn quiet ${s.currency}`}>
            <span className="muted">{t('header.currency')}</span>
            <select value={settings.displayCurrency} onChange={(e) => settingsStore.setDisplayCurrency(e.target.value as Currency)} aria-label={t('header.currency')}>
              {DISPLAY_CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
        )}
        <LanguageSelect className={`btn quiet ${s.currency}`} />
        {onExport && (
          <button type="button" className="btn quiet" onClick={onExport}>
            <IconDownload size={13} />
            {t('header.export')}
          </button>
        )}
        <button type="button" className="btn quiet" title={t('header.settings')} aria-label={t('header.settings')} onClick={() => navigate('/settings')}>
          <IconGear size={14} />
        </button>
      </div>
    </header>
  );
}
